import { AddItemsPickerButton } from "../../../../shared/orderLineForm/components/addItemsPickerStyles";
import { FulfilLineGrid, productLabel } from "../FulfilLineGrid";
import {
  SectionHint,
  SectionTitle,
  SelectionPill,
  StepFooter,
  StepResult,
  StepState,
} from "../FulfilParts";
import { totalQty, trimQty } from "../fulfilHelpers";
import { useFulfilStep } from "../useFulfilStep";
import { useStepSelection } from "../useStepSelection";

const maxQty = row => row.max_qty;
const defaultTarget = row => row.allocated_qty;

const COLUMNS = [
  { key: "line", label: "#", render: row => row.line_number },
  { key: "product", label: "Product", render: row => productLabel(row.product) },
  { key: "warehouse", label: "From", render: row => row.warehouse?.name || "—" },
  { key: "ordered", label: "Ordered", numeric: true, render: row => trimQty(row.quantity) },
  {
    key: "allocated",
    label: "Allocated",
    numeric: true,
    render: row => trimQty(row.allocated_qty),
  },
  { key: "free", label: "Free stock", numeric: true, render: row => trimQty(row.free_qty) },
];

/**
 * Step 1: reserve stock for this order's lines.
 *
 * @param {{orderId: (string|number), onNext: function()}} props
 */
const targetQtyError = (text, max) => {
  const trimmed = String(text ?? "").trim();
  if (trimmed === "") return "Enter a quantity";
  const number = Number(trimmed);
  if (!Number.isFinite(number) || number < 0) return "Enter zero or more";
  if (max !== undefined && max !== null && number > Number(max)) {
    return `No more than ${trimQty(max)}`;
  }
  return null;
};

export const AllocateStep = ({ orderId, onNext }) => {
  const { query, rows, run, saving, result, clearResult } = useFulfilStep(orderId, "allocate");
  const selection = useStepSelection(rows, {
    idKey: "line_id",
    defaultQty: defaultTarget,
    maxQty,
    qtyError: targetQtyError,
  });

  if (result) {
    return (
      <StepResult result={result} nextLabel="Next: Pick" onNext={onNext} onAgain={clearResult} />
    );
  }

  const count = selection.selectedRows.length;
  const total = trimQty(totalQty(selection.checked, selection.quantities));

  const submit = () =>
    run({
      lines: selection.selectedRows.map(row => ({
        line_id: row.line_id,
        qty: selection.quantities[row.line_id],
      })),
    });

  return (
    <>
      <SectionTitle>Allocate stock</SectionTitle>
      <SectionHint>
        Set how much stock to reserve on each line. Fully allocated lines stay here so you can lower
        the quantity or allocate more when stock is free.
      </SectionHint>
      <StepState query={query} emptyText="There are no lines to allocate on this order.">
        <FulfilLineGrid
          rows={rows}
          idKey="line_id"
          columns={COLUMNS}
          selection={selection}
          qtyLabel="Allocated qty"
          disabled={saving}
          ariaLabel="Lines to allocate"
        />
        <StepFooter pill={<SelectionPill selected={count} total={rows.length} units={total} />}>
          <AddItemsPickerButton
            type="primary"
            loading={saving}
            disabled={!count || selection.hasErrors || saving}
            onClick={submit}
          >
            Save allocation
          </AddItemsPickerButton>
        </StepFooter>
      </StepState>
    </>
  );
};
